import React, { useEffect, useState } from 'react';
import { X, Download, Calendar, TrendingUp, Users, FileText, BarChart3 } from 'lucide-react';
import { API_BASE, authFetch } from '../../constants';

function SampleTotalsModal({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [reportType, setReportType] = useState('ytd'); // 'ytd' or 'annual'
  const [generatingPDF, setGeneratingPDF] = useState(false);

  useEffect(() => {
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, selectedYear]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      
      // Currently only YTD is supported in the endpoint
      const endpoint = `${API_BASE}/reports/company-sample-totals/ytd`;
      
      const res = await authFetch(endpoint);
      
      if (!res.ok) {
        throw new Error('Failed to fetch report data');
      }
      
      const data = await res.json();
      setReportData(data);
    } catch (err) {
      console.error('Failed to fetch report:', err);
      alert('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setGeneratingPDF(true);
      
      const params = new URLSearchParams();
      if (reportType === 'annual') {
        params.append('year', selectedYear);
      }
      
      const res = await authFetch(
        `${API_BASE}/reports/company-sample-totals/generate-pdf?${params}`,
        { method: 'POST' }
      );
      
      if (!res.ok) {
        throw new Error('Failed to generate PDF');
      }
      
      // Download the PDF
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = reportType === 'ytd' 
        ? `Sample_Totals_${selectedYear}_YTD.pdf`
        : `Sample_Totals_${selectedYear}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      alert('PDF report downloaded successfully!');
    } catch (err) {
      console.error('Failed to generate PDF:', err);
      alert('Failed to generate PDF report');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const availableYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= currentYear - 5; year--) {
      years.push(year);
    }
    return years;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-4xl w-full mx-4">
          <div className="flex justify-center items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            <span className="ml-4 text-gray-700">Loading report data...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 my-8">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-green-600 to-green-700">
          <div className="flex items-center gap-3">
            <BarChart3 size={28} className="text-white" />
            <div>
              <h2 className="text-2xl font-bold text-white">Sample Totals Report</h2>
              <p className="text-green-100 text-sm">Company-specific sample analysis by grower</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-green-800 p-2 rounded-lg transition"
          >
            <X size={24} />
          </button>
        </div>

        {/* Report Controls */}
        <div className="p-6 bg-gray-50 border-b">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex gap-4 items-center">
              {/* Report Type Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar size={16} className="inline mr-1" />
                  Report Period
                </label>
                <select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="ytd">Year to Date {new Date().getFullYear()}</option>
                  <option value="annual">Full Year</option>
                </select>
              </div>

              {/* Year Selector (only for annual reports) */}
              {reportType === 'annual' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Year
                  </label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    {availableYears().map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Download PDF Button */}
            <button
              onClick={handleDownloadPDF}
              disabled={generatingPDF}
              className="flex items-center gap-2 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generatingPDF ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Generating PDF...
                </>
              ) : (
                <>
                  <Download size={20} />
                  Download PDF Report
                </>
              )}
            </button>
          </div>
        </div>

        {/* Report Summary */}
        {reportData && (
          <div className="p-6">
            {/* Company Info */}
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-green-800">
                <Users size={20} />
                <span className="font-semibold">Company:</span>
                <span>{reportData.company_name}</span>
              </div>
              <div className="text-sm text-green-700 mt-1">
                Report Period: {reportType === 'ytd' 
                  ? `January 1, ${reportData.year} - ${new Date().toLocaleDateString()}`
                  : `Full Year ${selectedYear}`
                }
              </div>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-blue-600 text-sm font-medium">Total Samples</div>
                <div className="text-3xl font-bold text-blue-900">{reportData.grand_total}</div>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="text-purple-600 text-sm font-medium">Active Growers</div>
                <div className="text-3xl font-bold text-purple-900">{reportData.summary?.length || 0}</div>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="text-green-600 text-sm font-medium">Average Samples/Grower</div>
                <div className="text-3xl font-bold text-green-900">
                  {reportData.summary?.length > 0 
                    ? Math.round(reportData.grand_total / reportData.summary.length)
                    : 0
                  }
                </div>
              </div>
            </div>

            {/* Top Growers Summary */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <TrendingUp size={20} className="text-green-600" />
                Top 10 Growers by Sample Volume
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grower Name</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Samples</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">% of Total</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Distribution</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reportData.summary?.slice(0, 10).map((grower, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">{index + 1}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{grower.grower_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right font-semibold">{grower.total_samples}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">{grower.percentage}%</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                              <div 
                                className="bg-green-600 h-2 rounded-full"
                                style={{ width: `${grower.percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {reportData.summary?.length > 10 && (
                <p className="text-sm text-gray-500 mt-3 text-center">
                  Showing top 10 of {reportData.summary.length} growers. Download PDF for complete report.
                </p>
              )}
            </div>

            {/* Download Instructions */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <FileText size={20} className="text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-blue-900 mb-1">PDF Report Contents</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• <strong>Summary Page:</strong> Complete list of all growers sorted by volume (highest to lowest) with sample counts and percentages</li>
                    <li>• <strong>Monthly Breakdown:</strong> Detailed month-by-month sample counts for each grower</li>
                    <li>• <strong>Running Totals:</strong> Year-to-date running totals displayed for each month</li>
                    <li>• <strong>Monthly Summaries:</strong> Total samples submitted each month with cumulative totals</li>
                  </ul>
                  <p className="text-sm text-blue-700 mt-2">
                    Click "Download PDF Report" above to generate the complete detailed report.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default SampleTotalsModal;
